<template>
  <div class="modern-container">
    <aside class="insights-aside">
      <field-insights-panel />
    </aside>

    <div
      class="feed-wrapper"
      :class="{ 'feed-wrapper--row': viewMode === 'row' }"
    >
      <div class="page-header">
        <div class="header-content">
          <h2 class="page-title">
            Publicaciones en ruta
          </h2>
          <p class="page-subtitle">
            Publicaciones del equipo en terreno
          </p>
        </div>
        <div class="header-search">
          <div class="search-input-wrapper">
            <i class="fa fa-search search-icon" />
            <input
              v-model="searchQuery"
              type="text"
              class="search-input"
              placeholder="Buscar por caption, usuario, tienda o categoría..."
              @input="onSearch"
            >
            <button
              v-if="searchQuery"
              class="search-clear"
              @click="clearSearch"
            >
              <i class="fa fa-times" />
            </button>
          </div>
        </div>
        <div class="header-actions">
          <div class="view-toggle">
            <button
              :class="['view-btn', { active: viewMode === 'list' }]"
              title="Vista lista"
              @click="viewMode = 'list'"
            >
              <i class="fa fa-list" />
            </button>
            <button
              :class="['view-btn', { active: viewMode === 'row' }]"
              title="Vista fila"
              @click="viewMode = 'row'"
            >
              <i class="fa fa-th-list" />
            </button>
          </div>
          <exportable-ppt />
          <button
            class="hdr-btn hdr-btn--icon"
            :disabled="loading"
            @click="refreshList"
          >
            <i
              class="fa fa-refresh"
              :class="{'fa-spin': loading}"
            />
          </button>
          <button
            class="hdr-btn hdr-btn--primary"
            @click="goToUpload"
          >
            <i class="fa fa-upload mr-1" />
            Subir
          </button>
        </div>
      </div>

      <hashtags-dashboard
        :selected-hashtag-id="activeHashtag ? activeHashtag.id : null"
        :refresh-trigger="dashboardRefreshKey"
        @filter="onHashtagFilter"
      />

      <transition name="chip-fade">
        <div
          v-if="activeHashtag"
          class="filter-chip"
          :style="{ '--chip-color': activeHashtag.color, '--chip-bg': activeHashtag.color + '18' }"
        >
          <span
            class="chip-dot"
            :style="{ background: activeHashtag.color }"
          />
          <span class="chip-text">
            Filtrando por <strong>#{{ activeHashtag.label }}</strong>
          </span>
          <button
            class="chip-clear"
            @click="clearHashtagFilter"
          >
            <i class="fa fa-times" />
          </button>
        </div>
      </transition>

      <div class="feed-column">
        <template v-if="loading && newsFeedList.length === 0">
          <div
            v-for="n in 3"
            :key="n"
            class="skeleton-card mb-2"
          >
            <div class="d-flex align-items-center mb-3">
              <div class="skeleton-avatar shimmer mr-2" />
              <div>
                <div class="skeleton-text medium shimmer mb-1" />
                <div class="skeleton-text short shimmer" />
              </div>
            </div>
            <div class="skeleton-item shimmer" />
          </div>
        </template>

        <template v-else-if="newsFeedList.length > 0">
          <component
            :is="post.isCheckEvent ? 'check-event-card' : 'news-card'"
            v-for="post in newsFeedList"
            :key="post.id"
            :content="post"
            :view-mode="viewMode"
            class="mb-2"
            @deleted="onPostDeleted"
            @check-in-deleted="onCheckInDeleted"
            @hashtag-added="onHashtagAdded"
          />
          <div ref="sentinel" />
          <div
            v-if="loadingMore"
            class="loading-more"
          >
            <b-spinner
              small
              variant="secondary"
            />
          </div>
          <p
            v-if="!hasMore && !loadingMore"
            class="end-of-feed"
          >
            · · ·
          </p>
        </template>

        <div
          v-else
          class="empty-state"
        >
          <i
            :class="['fa', 'fa-2x', 'mb-2', activeHashtag ? 'fa-hashtag' : 'fa-image']"
          />
          <p class="empty-title">
            Sin publicaciones
          </p>
          <p class="empty-subtitle">
            {{ activeHashtag
              ? `No hay publicaciones con #${activeHashtag.label} en el período seleccionado.`
              : 'El equipo en ruta aún no ha realizado publicaciones.'
            }}
          </p>
          <button
            v-if="activeHashtag"
            class="empty-clear-btn"
            @click="clearHashtagFilter"
          >
            <i class="fa fa-times mr-1" />
            Quitar filtro
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from '@vue/composition-api'
import { newsFeedList as fetchNewsFeedList } from '../Services'
import NewsFeedPost from '../models/NewsFeedPost'
import NewsCard from '../components/NewsCard'
import CheckEventCard from '../components/CheckEventCard'
import ExportablePpt from '../components/ExportablePpt'
import FieldInsightsPanel from '../components/FieldInsightsPanel'
import HashtagsDashboard from '../components/HashtagsDashboard'

export default {
  name: 'NewsFeed',
  components: {
    NewsCard,
    CheckEventCard,
    ExportablePpt,
    FieldInsightsPanel,
    HashtagsDashboard
  },
  setup (props, context) {
    const loading = ref(false)
    const loadingMore = ref(false)
    const newsFeedList = ref([])
    const currentPage = ref(1)
    const perPage = ref(20)
    const totalRows = ref(0)
    const hasMore = ref(true)
    const sentinel = ref(null)
    const searchQuery = ref('')
    const activeHashtag = ref(null)
    const dashboardRefreshKey = ref(0)
    const viewMode = ref('list')
    let observer = null
    let searchTimer = null

    const fetch = async (page, search = '') => {
      const filters = activeHashtag.value ? { hashtag_id: activeHashtag.value.id } : {}
      const rawResult = await fetchNewsFeedList(page, search, perPage.value, filters)
      const result = rawResult.map(post => new NewsFeedPost(post))
      if (result.length > 0) {
        totalRows.value = result[0].total
        perPage.value = result[0].per_page
        currentPage.value = page
        hasMore.value = newsFeedList.value.length + result.length < totalRows.value
      } else {
        hasMore.value = false
      }
      return result
    }

    const initialLoad = async () => {
      loading.value = true
      try {
        newsFeedList.value = await fetch(1, searchQuery.value)
      } finally {
        loading.value = false
      }
    }

    const loadMore = async () => {
      if (loadingMore.value || !hasMore.value) return
      loadingMore.value = true
      try {
        const result = await fetch(currentPage.value + 1, searchQuery.value)
        newsFeedList.value = [...newsFeedList.value, ...result]
      } finally {
        loadingMore.value = false
      }
    }

    const setupObserver = () => {
      observer = new IntersectionObserver(
        (entries) => { if (entries[0].isIntersecting) loadMore() },
        { rootMargin: '200px' }
      )
      if (sentinel.value) observer.observe(sentinel.value)
    }

    const resetAndLoad = async () => {
      if (observer) observer.disconnect()
      newsFeedList.value = []
      hasMore.value = true
      await initialLoad()
      context.root.$nextTick(setupObserver)
    }

    const onSearch = () => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => resetAndLoad(), 450)
    }

    const clearSearch = () => {
      searchQuery.value = ''
      resetAndLoad()
    }

    const goToUpload = () => context.root.$router.push({ name: 'UploadImagesFeed' })
    const goToExecutions = () => context.root.$router.push('/execution_focus')

    const refreshList = () => resetAndLoad()

    const onHashtagAdded = () => {
      dashboardRefreshKey.value++
    }

    const onHashtagFilter = (hashtag) => {
      activeHashtag.value = hashtag
      resetAndLoad()
    }

    const clearHashtagFilter = () => {
      activeHashtag.value = null
      resetAndLoad()
    }

    const onPostDeleted = (postId) => {
      const post = newsFeedList.value.find(p => p.id === postId)
      if (post) {
        const userInfo = context.root.$store.getters.userInfo || {}
        post.is_deleted = true
        post.deleted_by_name = userInfo.name || 'Usuario'
      }
    }

    const onCheckInDeleted = (checkInId) => {
      const userInfo = context.root.$store.getters.userInfo || {}
      newsFeedList.value.forEach(post => {
        if (post.id === checkInId || post.check_in_id === checkInId) {
          post.is_deleted = true
          post.deleted_by_name = userInfo.name || 'Usuario'
        }
      })
    }

    onMounted(async () => {
      await initialLoad()
      context.root.$nextTick(setupObserver)
    })

    onUnmounted(() => {
      if (observer) observer.disconnect()
      clearTimeout(searchTimer)
    })

    return {
      loading,
      loadingMore,
      newsFeedList,
      hasMore,
      sentinel,
      searchQuery,
      activeHashtag,
      dashboardRefreshKey,
      viewMode,
      onSearch,
      clearSearch,
      goToUpload,
      goToExecutions,
      refreshList,
      onPostDeleted,
      onCheckInDeleted,
      onHashtagFilter,
      clearHashtagFilter,
      onHashtagAdded
    }
  }
}
</script>

<style scoped>
.modern-container {
  background: #f5f7fa;
  min-height: 100vh;
  padding: 1.25rem;
}

.insights-aside {
  position: fixed;
  left: 1.25rem;
  top: calc(55px + 1.25rem);
  width: 260px;
  max-height: calc(100vh - 55px - 2.5rem);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 transparent;
  z-index: 10;
}

.insights-aside::-webkit-scrollbar {
  width: 4px;
}

.insights-aside::-webkit-scrollbar-track {
  background: transparent;
}

.insights-aside::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 2px;
}

.feed-wrapper {
  max-width: 680px;
  margin: 0 auto;
  transition: max-width 0.25s ease;
}

.feed-wrapper--row {
  max-width: 900px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding: 0.875rem 1.25rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.header-search {
  flex: 1;
  max-width: 340px;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 0.6rem;
  color: #a0aec0;
  font-size: 0.75rem;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.38rem 2rem 0.38rem 1.9rem;
  font-size: 0.78rem;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #f8fafc;
  color: #2d3748;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.search-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.12);
  background: white;
}

.search-input::placeholder {
  color: #cbd5e0;
}

.search-clear {
  position: absolute;
  right: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  color: #a0aec0;
  font-size: 0.72rem;
  padding: 0;
  line-height: 1;
  display: flex;
  align-items: center;
}

.search-clear:hover {
  color: #4a5568;
}

.page-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.page-subtitle {
  font-size: 0.72rem;
  color: #a0aec0;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

/* Header buttons */
.hdr-btn {
  display: inline-flex;
  align-items: center;
  border: none;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 500;
  border-radius: 20px;
  transition: background 0.15s, opacity 0.15s;
  line-height: 1;
}

.hdr-btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.hdr-btn--icon {
  background: #edf2f7;
  color: #4a5568;
  width: 32px;
  height: 32px;
  justify-content: center;
  padding: 0;
  border-radius: 50%;
}

.hdr-btn--icon:hover:not(:disabled) {
  background: #e2e8f0;
}

.hdr-btn--primary {
  background: #edf2f7;
  color: #4a5568;
  padding: 0.38rem 0.9rem;
}

.hdr-btn--primary:hover {
  background: #e2e8f0;
}

/* View toggle */
.view-toggle {
  display: flex;
  background: #edf2f7;
  border-radius: 20px;
  padding: 0.2rem;
  gap: 0.1rem;
}

.view-btn {
  background: transparent;
  border: none;
  padding: 0.25rem 0.55rem;
  font-size: 0.75rem;
  color: #718096;
  cursor: pointer;
  border-radius: 16px;
  transition: background 0.15s, color 0.15s;
  line-height: 1;
  display: flex;
  align-items: center;
}

.view-btn:hover {
  color: #2d3748;
}

.view-btn.active {
  background: white;
  color: #667eea;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.feed-column {
  flex: 1;
  min-width: 0;
}

/* Skeleton */
.skeleton-card {
  background: white;
  border-radius: 10px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
}

@keyframes shimmer {
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
}

.shimmer {
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 5px;
}

.skeleton-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
}

.skeleton-item {
  height: 90px;
  width: 100%;
  border-radius: 6px;
}

.skeleton-text { height: 0.7rem; }
.skeleton-text.short { width: 70px; }
.skeleton-text.medium { width: 130px; }

.loading-more {
  display: flex;
  justify-content: center;
  padding: 0.75rem 0;
}

.end-of-feed {
  text-align: center;
  color: #cbd5e0;
  font-size: 1.1rem;
  letter-spacing: 0.4em;
  padding: 1rem 0 1.5rem;
  margin: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
  color: #a0aec0;
  text-align: center;
}

.empty-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #4a5568;
  margin: 0.5rem 0 0.2rem 0;
}

.empty-subtitle {
  font-size: 0.78rem;
  color: #718096;
  margin: 0;
}

.empty-clear-btn {
  margin-top: 0.75rem;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  padding: 0.35rem 0.85rem;
  font-size: 0.75rem;
  color: #718096;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.empty-clear-btn:hover {
  border-color: #a0aec0;
  color: #4a5568;
}

/* Filter chip */
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--chip-bg, #eef2ff);
  border: 1px solid var(--chip-color, #4299e1);
  border-radius: 20px;
  padding: 0.3rem 0.5rem 0.3rem 0.65rem;
  margin-bottom: 0.75rem;
  font-size: 0.76rem;
}

.chip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.chip-text {
  color: #2d3748;
}

.chip-text strong {
  color: var(--chip-color, #4299e1);
}

.chip-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0.1rem;
  cursor: pointer;
  color: #a0aec0;
  font-size: 0.65rem;
  line-height: 1;
  transition: color 0.15s;
}

.chip-clear:hover {
  color: #4a5568;
}

/* Chip transition */
.chip-fade-enter-active,
.chip-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.chip-fade-enter,
.chip-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (max-width: 768px) {
  .modern-container { padding: 0.75rem; }

  .columns { flex-direction: column; }

  .sidebar-column {
    width: 100%;
    position: static;
  }
}
</style>



// WEBPACK FOOTER //
// src/app/news_feed/views/NewsFeed.vue